import { TradeLeadCalculator } from "@/components/TradeLeadCalculator";
import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "Why isn't my garage door company showing up on Google in Colorado?",
    a: "Most Colorado garage door companies don't show up because their Google Business Profile is missing categories (Garage Door Supplier + Garage Door Repair Service), they have fewer than 30 reviews, and their site has no service-specific or city-specific pages. National franchises like Precision dominate on volume — independents win by going neighborhood-deep on emergency repair searches. Full guide: /answers/why-isnt-my-garage-door-company-showing-up-on-google.",
  },
  {
    q: "Can a one-truck garage door shop compete with Precision in Colorado Springs?",
    a: "On emergency calls and neighborhood-level searches, yes. National franchises win on Map Pack share across the metro, but independents win on speed, price, and tight 10–15 mile radius dominance. We build campaigns specifically around that local-radius win.",
  },
  {
    q: "How long does garage door SEO take to work?",
    a: "Map Pack movement on emergency-repair searches usually starts in 60–90 days. Organic rankings on competitive terms like 'garage door repair Colorado Springs' typically take 4–6 months of consistent review velocity and on-page work.",
  },
  {
    q: "Should I run Google Ads for garage door work?",
    a: "Yes — for emergency repair searches specifically (broken springs, jammed doors, openers won't open). CPCs are high but call-conversion is high because the searcher needs help today. We tightly geo-target campaigns on emergency keywords only.",
  },
  {
    q: "How do I separate new-door installation from repair work?",
    a: "Build dedicated pages for each: spring repair, opener install, new door install, emergency repair. Each page targets a different searcher and a different price point. New-door installation is the highest-ticket work and deserves its own page — especially in Springs neighborhoods like Northgate and Black Forest with three-car garages.",
  },
];

export const Route = createFileRoute("/garage-door-marketing-colorado")({
  head: () =>
    industryMarketingHead("Garage Door Company", "garage door company", "garage-door-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry="Garage Door Company"
      industryLower="garage door company"
      slug="garage-door-marketing-colorado"
      extraSections={<TradeLeadCalculator industry="Garage Door" defaultJob={650} />}
      subheadline="We help Colorado independent garage door shops outrank national franchises — on the emergency calls that pay the bills."
      painPoints={[
        "Precision and the national franchises crowd the Map Pack across the metro",
        "Emergency 'broken spring' calls go to whoever ranks first",
        "New-door installation work is buried under generic 'garage door services'",
      ]}
      serviceDescriptions={{
        seo: "Rank for spring repair, opener install, new door installation, and emergency calls across every Colorado market you serve.",
        googleMaps: "Dominate the local Map Pack so emergency garage door calls come to your truck first.",
        webDesign: "Fast, mobile-first garage door sites with click-to-call above the fold and 5-star review walls.",
        ppc: "Profitable Google Ads tuned to emergency-repair queries — the highest-intent search in the category.",
        geoAi: "Get cited when Coloradans ask ChatGPT 'who fixes garage doors near me' or 'broken spring help'.",
        socialMedia: "Real installation content that builds the trust national franchises can't manufacture.",
      }}
      testimonial={{
        quote:
          "We were getting buried by Precision on every search. Now we're booked solid on emergency calls within a 10-mile radius — and that's exactly the work we want.",
        role: "Independent Garage Door Shop Owner",
        city: "Colorado Springs",
      }}
      faqs={FAQS}
      cityHubLinks={[
        { to: "/colorado-springs", label: "Garage door marketing in Colorado Springs", description: "Help Springs independents own neighborhood searches across Briargate, Powers, Northgate, and Black Forest." },
        { to: "/denver-digital-marketing", label: "Garage door marketing in Denver", description: "Compete in metro Denver — emergency repair and new-door install across the south and west metro." },
        { to: "/castle-rock-digital-marketing", label: "Garage door marketing in Castle Rock", description: "Premium new-door installation across Castle Rock, Castle Pines, and Parker." },
      ]}
    />
  ),
});
